// Authentication Error Keys for URL parameters and user-facing messages
// Generic sequential codes that don't reveal system internals but allow differentiation
export const AUTH_ERROR_KEYS = {
  CONNECTION_FAILED: 'error1',
  SERVICE_UNAVAILABLE: 'error2',
  NETWORK_TIMEOUT: 'error3',
  SESSION_CORRUPTED: 'error4',
  SECURITY_VALIDATION_FAILED: 'error5',
  BROWSER_SECURITY_BLOCKED: 'error6',
  LOGIN_PROCESSING_FAILED: 'error7',
  UNKNOWN: 'unknown',
} as const

// Authentication Flow Stages for logging categorization
export const AUTH_STAGES = {
  JWT_VALIDATION: 'jwt_validation',
  SESSION_MANAGEMENT: 'session_management',
  PKCE_VALIDATION: 'pkce_validation',
  OAUTH_TOKEN_REQUEST: 'oauth_token_request',
  OAUTH_TOKEN_RESPONSE: 'oauth_token_response',
  OAUTH_TOKEN_NETWORK: 'oauth_token_network',
  PREFLIGHT_CHECK: 'preflight_check',
  TOKEN_REFRESH: 'token_refresh',
} as const

// OAuth 2.0 Error Codes (RFC 6749)
// https://datatracker.ietf.org/doc/html/rfc6749#section-5.2
export const OAUTH_ERROR_CODES = {
  INVALID_GRANT: 'invalid_grant', // The provided authorization grant or refresh token is invalid, expired, revoked
  INVALID_REQUEST: 'invalid_request', // The request is missing a required parameter
  INVALID_CLIENT: 'invalid_client', // Client authentication failed
  UNAUTHORIZED_CLIENT: 'unauthorized_client', // The client is not authorized
  UNSUPPORTED_GRANT_TYPE: 'unsupported_grant_type', // The authorization grant type is not supported
} as const

// Failure Reasons for detailed error categorization
export const FAILURE_REASONS = {
  JWT_PARSE_ERROR: 'jwt_parse_error',
  JWT_EXPIRED: 'jwt_expired',
  AUTHORIZATION_ERROR: 'authorization_error',
  STATE_MISMATCH: 'state_mismatch',
  HTTP_ERROR: 'http_error',
  NETWORK_ERROR: 'network_error',
  MISSING_ID_TOKEN: 'missing_id_token',
  PREFLIGHT_ERROR: 'preflight_error',
  REFRESH_TOKEN_EXPIRED: 'refresh_token_expired',
  REFRESH_TOKEN_INVALID: 'refresh_token_invalid',
} as const

// Error Types for high-level error classification
export const ERROR_TYPES = {
  JWT_PARSING_FAILURE: 'jwt_parsing_failure',
  SESSION_CLEAR_EVENT: 'session_clear_event',
  PKCE_VALIDATION_FAILURE: 'pkce_validation_failure',
  TOKEN_EXCHANGE_FAILURE: 'token_exchange_failure',
  PREFLIGHT_FAILURE: 'preflight_failure',
  TOKEN_REFRESH_FAILURE: 'token_refresh_failure',
} as const

// Type exports for better TypeScript support
export type AuthErrorKey = (typeof AUTH_ERROR_KEYS)[keyof typeof AUTH_ERROR_KEYS]
export type AuthStage = (typeof AUTH_STAGES)[keyof typeof AUTH_STAGES]
export type OAuthErrorCode = (typeof OAUTH_ERROR_CODES)[keyof typeof OAUTH_ERROR_CODES]
export type FailureReason = (typeof FAILURE_REASONS)[keyof typeof FAILURE_REASONS]
export type ErrorType = (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES]
